import React, { useState, useEffect } from 'react';
import { Save, Loader2, Plus, Trash2, GripVertical } from 'lucide-react';
import { useApi } from '../AdminApp';

const skillCategories = [
  { value: 'frontend', label: 'Frontend' },
  { value: 'backend', label: 'Backend' },
  { value: 'ai', label: 'AI/ML' },
  { value: 'tools', label: 'Tools' },
  { value: 'other', label: 'Other' }
];

const AboutEditor = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tr');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [aboutData, setAboutData] = useState({
    title: { tr: '', en: '', de: '' },
    description: { tr: '', en: '', de: '' },
    skills: [],
    experience: 0,
    projectsCount: 0
  });

  const [newSkill, setNewSkill] = useState({ name: '', level: 50, category: 'other' });

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const data = await apiCall('/about');
      setAboutData(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await apiCall('/about', {
        method: 'PUT',
        body: JSON.stringify(aboutData)
      });
      setMessage({ type: 'success', text: 'About section updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value, lang = null) => {
    if (lang) {
      setAboutData(prev => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value }
      }));
    } else {
      setAboutData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addSkill = () => {
    if (!newSkill.name.trim()) return;
    setAboutData(prev => ({
      ...prev,
      skills: [...prev.skills, { ...newSkill, _id: Date.now().toString() }]
    }));
    setNewSkill({ name: '', level: 50, category: 'other' });
  };

  const updateSkill = (index, field, value) => {
    setAboutData(prev => ({
      ...prev,
      skills: prev.skills.map((skill, i) =>
        i === index ? { ...skill, [field]: value } : skill
      )
    }));
  };

  const removeSkill = (index) => {
    setAboutData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const tabs = [
    { id: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { id: 'en', label: 'English', flag: '🇺🇸' },
    { id: 'de', label: 'Deutsch', flag: '🇩🇪' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">About Section</h1>
          <p className="text-gray-400 mt-1">Edit your about information and skills</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Language Content */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
        <div className="flex border-b border-gray-700/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition ${
                activeTab === tab.id
                  ? 'text-white bg-gray-700/50 border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
              }`}
            >
              <span>{tab.flag}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title ({activeTab.toUpperCase()})
            </label>
            <input
              type="text"
              value={aboutData.title[activeTab] || ''}
              onChange={(e) => handleChange('title', e.target.value, activeTab)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              placeholder="About Me"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description ({activeTab.toUpperCase()})
            </label>
            <textarea
              value={aboutData.description[activeTab] || ''}
              onChange={(e) => handleChange('description', e.target.value, activeTab)}
              rows={6}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
              placeholder="Write about yourself..."
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Statistics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Years of Experience
            </label>
            <input
              type="number"
              value={aboutData.experience || 0}
              onChange={(e) => handleChange('experience', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Number of Projects
            </label>
            <input
              type="number"
              value={aboutData.projectsCount || 0}
              onChange={(e) => handleChange('projectsCount', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Skills */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Skills</h3>

        {/* Add New Skill */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 p-4 bg-gray-700/30 rounded-xl">
          <input
            type="text"
            value={newSkill.name}
            onChange={(e) => setNewSkill(prev => ({ ...prev, name: e.target.value }))}
            className="flex-1 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="Skill name (e.g., Python)"
          />
          <select
            value={newSkill.category}
            onChange={(e) => setNewSkill(prev => ({ ...prev, category: e.target.value }))}
            className="px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          >
            {skillCategories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <input
            type="number"
            value={newSkill.level}
            onChange={(e) => setNewSkill(prev => ({ ...prev, level: parseInt(e.target.value) || 0 }))}
            className="w-24 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="%"
            min="0"
            max="100"
          />
          <button
            onClick={addSkill}
            className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-3 rounded-xl font-medium hover:bg-green-600 transition"
          >
            <Plus className="w-5 h-5" />
            Add
          </button>
        </div>

        {/* Skills List */}
        <div className="space-y-3">
          {aboutData.skills.map((skill, index) => (
            <div key={skill._id || index} className="flex items-center gap-3 p-4 bg-gray-700/30 rounded-xl group">
              <GripVertical className="w-5 h-5 text-gray-500 cursor-grab" />
              <input
                type="text"
                value={skill.name}
                onChange={(e) => updateSkill(index, 'name', e.target.value)}
                className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <select
                value={skill.category}
                onChange={(e) => updateSkill(index, 'category', e.target.value)}
                className="px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                {skillCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <div className="flex items-center gap-2 w-40">
                <input
                  type="range"
                  value={skill.level}
                  onChange={(e) => updateSkill(index, 'level', parseInt(e.target.value))}
                  className="flex-1"
                  min="0"
                  max="100"
                />
                <span className="text-white w-10 text-right">{skill.level}%</span>
              </div>
              <button
                onClick={() => removeSkill(index)}
                className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
          {aboutData.skills.length === 0 && (
            <p className="text-center text-gray-400 py-8">No skills added yet. Add your first skill above.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutEditor;
